<template>
  <div class="gs-aff-panel">
    <header class="gs-aff-panel__head">
      <div>
        <h3>Custom landing pages</h3>
        <p>Extra pages affiliates can send traffic to, such as webinar registrations or launch pages. Their links still credit the affiliate.</p>
      </div>
      <gs-button type="button" variant="primary" size="sm" @click="emitCreateAffiliateLandingPage">
        <gs-icon name="plus" size="14"></gs-icon>
        {{ $t('frontend_product_step9_add_new_landing_page') }}
      </gs-button>
    </header>

    <div v-if="localModel.affiliate_landing_pages.length > 6" class="gs-aff-panel__meta">
      <label class="gs-aff-search">
        <gs-icon name="search" size="16"></gs-icon>
        <input v-model="search" type="search" placeholder="Search landing pages" aria-label="Search landing pages" />
      </label>
      <span class="gs-aff-count">{{ localModel.affiliate_landing_pages.length }} landing pages</span>
    </div>

    <div class="gs-aff-list">
      <article
        v-for="{ page, index } in visiblePages"
        :key="page.id"
        class="gs-aff-row is-configured"
        :class="{ 'is-open': openId === page.id, 'has-error': hasError(index) }"
      >
        <button
          type="button"
          class="gs-aff-row__head"
          :aria-expanded="openId === page.id ? 'true' : 'false'"
          @click="toggle(page.id)"
        >
          <span class="gs-aff-row__dot" aria-hidden="true"></span>
          <span class="gs-aff-row__title">
            <span class="gs-aff-row__name">{{ page.description || 'Untitled landing page' }}</span>
            <span class="gs-aff-row__summary">{{ page.landing_page || 'No URL yet' }}</span>
            <affiliate-pills v-if="openId !== page.id && assignedType(page) === 2 && page.assigned_to.length" :affiliates="page.assigned_to" />
          </span>
          <span class="gs-aff-row__meta">
            <span v-if="index === 0" class="gs-aff-pill gs-aff-pill--active">Main page</span>
            <span v-if="hasError(index)" class="gs-aff-pill gs-aff-pill--danger">Needs attention</span>
            <span v-else class="gs-aff-pill">{{ availabilityLabel(page) }}</span>
          </span>
          <span class="gs-aff-row__chevron" aria-hidden="true">
            <gs-icon :name="openId === page.id ? 'chevron-up' : 'chevron-down'" size="18"></gs-icon>
          </span>
        </button>

        <div v-if="openId === page.id" class="gs-aff-row__body">
          <div class="gs-aff-grid">
            <div class="gs-aff-field">
              <span class="gs-aff-field__label">{{ $t('frontend_name') }}</span>
              <text-input
                v-model="page.description"
                :validator="validation.localModel.affiliate_landing_pages.$each[index].description"
                :attribute="$t('frontend_groovesell_landing_page_name')"
                value="page.description"
                :placeholder="$t('frontend_product_step9_name')"
              />
            </div>
            <div class="gs-aff-field">
              <span class="gs-aff-field__label">{{ $t('frontend_product_step9_landing_page') }}</span>
              <template v-if="index === 0">
                <span class="gs-aff-field__static" :title="page.landing_page">{{ page.landing_page }}</span>
                <span class="gs-aff-field__hint">{{ $t('frontend_product_step9_this_cannotbe') }}</span>
              </template>
              <text-input
                v-else
                v-model="page.landing_page"
                :validator="validation.localModel.affiliate_landing_pages.$each[index].landing_page"
                :attribute="$t('frontend_product_step9_landing_page_url')"
                value="page.landing_page"
                :placeholder="$t('frontend_product_step9_landing_page_url')"
              />
            </div>
          </div>

          <section class="gs-aff-section">
            <div class="gs-aff-section__head">
              <div>
                <strong>{{ $t('frontend_product_step9_available_to') }}</strong>
                <p v-if="index === 0">The main page is always available to every affiliate.</p>
                <p v-else>Choose who sees this page in their promo tools.</p>
              </div>
            </div>
            <div v-if="index > 0" class="gs-aff-choices" role="radiogroup" :aria-label="$t('frontend_product_step9_available_to')">
              <button
                type="button"
                role="radio"
                class="gs-aff-choice"
                :class="{ 'is-active': assignedType(page) === 1 }"
                :aria-checked="assignedType(page) === 1 ? 'true' : 'false'"
                @click="updateLandingPageAssignedType(page, 1)"
              >
                <span class="gs-aff-choice__radio" aria-hidden="true"></span>
                <span>
                  <strong>{{ $t('frontend_product_step9_all_affiliates') }}</strong>
                  <span>{{ $t('frontend_product_step9_this_landingpage') }}</span>
                </span>
              </button>
              <button
                type="button"
                role="radio"
                class="gs-aff-choice"
                :class="{ 'is-active': assignedType(page) === 2 }"
                :aria-checked="assignedType(page) === 2 ? 'true' : 'false'"
                @click="updateLandingPageAssignedType(page, 2)"
              >
                <span class="gs-aff-choice__radio" aria-hidden="true"></span>
                <span>
                  <strong>{{ $t('frontend_product_step9_specific_affiliates') }}</strong>
                  <span>{{ $t('frontend_product_step9_this_landingpage_wouldbe') }}</span>
                </span>
              </button>
            </div>
            <div v-if="index > 0 && assignedType(page) === 2" class="gs-aff-selected-summary">
              <affiliate-pills v-if="page.assigned_to.length" :affiliates="page.assigned_to" />
              <p v-else class="gs-aff-field__hint">No affiliates selected. Choose who can access this page.</p>
              <div class="gs-aff-actions">
              <gs-button type="button" variant="outline" size="sm" @click="openCustomLandingPagesAffModal(index)">
                <gs-icon name="users" size="14"></gs-icon>
                {{ page.assigned_to.length ? `Manage ${page.assigned_to.length} selected affiliate${page.assigned_to.length === 1 ? '' : 's'}` : 'Choose affiliates' }}
              </gs-button>
              </div>
            </div>
          </section>

          <div v-if="index > 0" class="gs-aff-actions">
            <button type="button" class="gs-aff-link-button" @click="emitDeleteAffiliateLandingPage(index)">
              Delete this landing page
            </button>
          </div>
        </div>
      </article>

      <div v-if="!visiblePages.length" class="gs-aff-empty">
        <strong>No matching landing pages</strong>
        <p>Try a different search.</p>
      </div>
    </div>
  </div>
</template>

<script>
import AffiliatePills from './AffiliatePills.vue';
export default {
  components: { AffiliatePills },
  props: {
    model: {
      type: Object,
      required: true
    },
    validation: {
      required: true
    },
    openCustomLandingPagesAffModal: {
      type: Function,
      required: true
    },
  },
  data() {
    const pages = this.model.affiliate_landing_pages || [];
    return {
      localModel: this.model,
      openId: pages.length ? pages[0].id : null,
      search: '',
    };
  },
  computed: {
    visiblePages() {
      const term = this.search.trim().toLowerCase();
      return this.localModel.affiliate_landing_pages
        .map((page, index) => ({ page, index }))
        .filter(({ page }) => !term
          || String(page.description || '').toLowerCase().includes(term)
          || String(page.landing_page || '').toLowerCase().includes(term));
    },
    pageCount() {
      return this.localModel.affiliate_landing_pages.length;
    },
  },
  watch: {
    localModel: {
      handler() {
        this.$emit('update-model', this.localModel);
      },
      deep: true
    },
    pageCount(count, previous) {
      if (count > previous) {
        // Open the page that was just added so it can be named right away.
        this.search = '';
        this.openId = this.localModel.affiliate_landing_pages[count - 1].id;
      }
    },
  },
  methods: {
    toggle(id) {
      this.openId = this.openId === id ? null : id;
    },
    hasError(index) {
      const pages = this.validation && this.validation.localModel && this.validation.localModel.affiliate_landing_pages;
      const entry = pages && pages.$each && pages.$each[index];
      return Boolean(entry && entry.$error);
    },
    assignedType(page) {
      const assignedTo = page.assigned_to || [];
      return assignedTo.length > 0 && assignedTo[0].id == 0 ? 1 : 2;
    },
    availabilityLabel(page) {
      if (this.assignedType(page) === 1) {
        return 'All affiliates';
      }
      const count = (page.assigned_to || []).length;
      return count ? `${count} affiliate${count === 1 ? '' : 's'}` : 'No affiliates yet';
    },
    updateLandingPageAssignedType(page, type) {
      if (this.assignedType(page) === type) {
        return;
      }
      page.assigned_to = type === 1
        ? [{ id: 0, name: this.$t('frontend_affiliates_all_affiliates') }]
        : [];
    },
    emitCreateAffiliateLandingPage() {
      this.$emit('create-affiliate-landing-page');
    },
    emitDeleteAffiliateLandingPage(index) {
      this.$emit('delete-affiliate-landing-page', index);
    },
  },
};
</script>
<style scoped>
.gs-aff-selected-summary { display:flex; flex-direction:column; gap:12px; margin-top:16px; }
.gs-aff-selected-summary .gs-aff-actions { margin-top:0; }
.gs-aff-row__title .gs-affiliate-pills { margin-top:8px; }
</style>
